/**
 * UI Slice
 *
 * Manages UI-related state such as scroll preferences, input draft,
 * focus state, and feature toggles (web search, deep research).
 */

import type { StateCreator } from 'zustand';
import type { ChatState, UISlice } from '../types';

/**
 * Initial state for the UI slice
 */
export const uiInitialState: Pick<
  UISlice,
  | 'autoScrollEnabled'
  | 'draftMessage'
  | 'focusInputRequested'
  | 'isWebSearchEnabled'
  | 'isDeepResearchEnabled'
  | 'newChatTrigger'
> = {
  autoScrollEnabled: true,
  draftMessage: '',
  focusInputRequested: false,
  isWebSearchEnabled: false,
  isDeepResearchEnabled: false,
  newChatTrigger: false,
};

/**
 * Creates the UI slice with UI state management actions.
 */
export const createUISlice: StateCreator<
  ChatState,
  [
    ['zustand/devtools', never],
    ['zustand/persist', unknown],
    ['zustand/immer', never],
  ],
  [],
  UISlice
> = set => ({
  // Initial state
  ...uiInitialState,

  // Actions
  setAutoScrollEnabled: (enabled: boolean) => {
    set({ autoScrollEnabled: enabled }, false, 'ui/setAutoScrollEnabled');
  },

  setDraftMessage: (text: string) => {
    set({ draftMessage: text }, false, 'ui/setDraftMessage');
  },

  requestFocusInput: () => {
    set({ focusInputRequested: true }, false, 'ui/requestFocusInput');
  },

  setFocusInputRequested: (requested: boolean) => {
    set({ focusInputRequested: requested }, false, 'ui/setFocusInputRequested');
  },

  setWebSearchEnabled: (enabled: boolean) => {
    set({ isWebSearchEnabled: enabled }, false, 'ui/setWebSearchEnabled');
  },

  setDeepResearchEnabled: (enabled: boolean) => {
    set({ isDeepResearchEnabled: enabled }, false, 'ui/setDeepResearchEnabled');
  },
});
